import React, { useEffect, useState } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const triggerOptions = [
  { value: 'temp_above', label: 'Temperature above', needsThreshold: true, placeholder: 'e.g. 95' },
  { value: 'temp_below', label: 'Temperature below', needsThreshold: true, placeholder: 'e.g. 45' },
  { value: 'feels_like_above', label: 'Feels-like / heat index above', needsThreshold: true, placeholder: 'e.g. 100' },
  { value: 'ride_heat_safety', label: 'Ride safety / best ride window', needsThreshold: false, defaultAction: 'Ride early today', defaultLookahead: 24 },
  { value: 'rain_expected', label: 'Rain expected', needsThreshold: true, placeholder: 'Rain chance %, e.g. 60' },
  { value: 'thunderstorm_expected', label: 'Thunderstorm expected', needsThreshold: false },
  { value: 'wind_above', label: 'Wind above', needsThreshold: true, placeholder: 'mph, e.g. 25' },
  { value: 'freeze_expected', label: 'Freeze expected', needsThreshold: false },
  { value: 'official_alert', label: 'Official weather alert', needsThreshold: false },
];


const formatWeatherHorseSummary = (horseNames = []) => {
  const names = Array.isArray(horseNames) ? horseNames.filter(Boolean) : [];
  if (names.length === 0) return 'Applies to this weather rule';
  if (names.length === 1) return `Applies to ${names[0]}`;
  if (names.length === 2) return `Applies to ${names[0]} and ${names[1]}`;
  if (names.length === 3) return `Applies to ${names[0]}, ${names[1]}, and ${names[2]}`;
  return `Applies to ${names[0]}, ${names[1]}, ${names[2]} +${names.length - 3} more`;
};

const weatherTitle = (e) => e?.title && e.title !== 'Weather Watch' ? e.title : 'Weather Watch';

const actionOptions = [
  'Bring horses in',
  'Turn horses out',
  'Blanket horses',
  'Remove blankets',
  'Check water',
  'Turn fans on / check fans',
  'Close barn doors/windows',
  'Check footing / arena',
  'Ride early today',
  'Not safe to ride',
  'Light work only',
  'Ride indoors / covered arena',
];

const WeatherWatchPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ locations: [], rules: [], horses: [], events: [] });
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [checking, setChecking] = useState(false);
  const [locationForm, setLocationForm] = useState({ name: 'Home Barn', query: '' });
  const [ruleForm, setRuleForm] = useState({
    name: '',
    trigger_type: 'temp_above',
    threshold_value: '',
    lookahead_hours: 6,
    notify_minutes_before: 60,
    action_text: 'Bring horses in',
    applies_to_all_horses: true,
    horse_ids: [],
  });

  const load = async () => {
    try {
      const res = await api.get('/weather-watch');
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Weather Watch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const defaultLocation = data.locations?.[0];
  const selectedTrigger = triggerOptions.find(t => t.value === ruleForm.trigger_type) || triggerOptions[0];

  const saveLocation = async () => {
    if (!locationForm.query.trim()) return toast.error('Enter a barn address, city, ZIP, or coordinates');
    setSavingLocation(true);
    try {
      const res = await api.post('/weather-watch/location', locationForm);
      if (res.data?.geocoding_note) {
        toast.success(res.data.geocoding_note, { duration: 7000 });
      } else {
        toast.success('Weather location saved');
      }
      setLocationForm({ name: 'Home Barn', query: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save location');
    } finally { setSavingLocation(false); }
  };

  const toggleHorse = (id) => {
    setRuleForm(prev => ({
      ...prev,
      horse_ids: prev.horse_ids.includes(id) ? prev.horse_ids.filter(x => x !== id) : [...prev.horse_ids, id]
    }));
  };

  const createRule = async () => {
    if (!defaultLocation) return toast.error('Save a weather location first');
    if (!ruleForm.name.trim()) return toast.error('Name the rule');
    if (!ruleForm.action_text.trim()) return toast.error('Add the action to show on Home');
    setSavingRule(true);
    try {
      await api.post('/weather-watch/rules', {
        ...ruleForm,
        location_id: defaultLocation.id,
        threshold_value: selectedTrigger.needsThreshold ? ruleForm.threshold_value : null,
      });
      toast.success('Weather rule saved');
      setRuleForm({
        name: '', trigger_type: 'temp_above', threshold_value: '', lookahead_hours: 6,
        notify_minutes_before: 60, action_text: 'Bring horses in', applies_to_all_horses: true, horse_ids: []
      });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save rule');
    } finally { setSavingRule(false); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this Weather Watch rule?')) return;
    try {
      await api.delete(`/weather-watch/rules/${id}`);
      toast.success('Rule deleted');
      await load();
    } catch { toast.error('Failed to delete rule'); }
  };


  const handleEventAction = async (eventId, action) => {
    try {
      if (action === 'snooze') await api.put(`/weather-watch/events/${eventId}/snooze`, { minutes: 120 });
      else if (action === 'handled') await api.put(`/weather-watch/events/${eventId}/handled`);
      else await api.put(`/weather-watch/events/${eventId}/dismiss`);
      toast.success(action === 'snooze' ? 'Reminder snoozed' : action === 'handled' ? 'Marked handled' : 'Dismissed for today');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Weather action failed');
    }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await api.post('/weather-watch/check-now');
      toast.success(res.data?.created ? `Created ${res.data.created} weather notice${res.data.created !== 1 ? 's' : ''}` : 'Weather checked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Weather check failed');
    } finally { setChecking(false); }
  };

  if (loading) return <div><TopBar title="Weather Watch" showBack backTo="/horses" /><div className="loading-screen"><div className="spinner" /><p>Loading...</p></div></div>;

  return (
    <div>
      <TopBar title="Weather Watch" subtitle="Barn-level weather rules for horse-care actions" showBack backTo="/horses" />
      <div className="page-content">
        <div className="card" style={{ border: '1px solid var(--cr-primary)', background: 'var(--cr-primary-light)' }}>
          <h2 style={{ fontSize: 16, marginBottom: 4 }}>Weather Watch setup</h2>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 0 }}>
            Create weather rules here. Short action alerts appear on the Home page as <strong>Weather Watch</strong> notices.
          </p>
        </div>

        <div className="section-label">Barn weather location</div>
        {defaultLocation && (
          <div className="card" style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{defaultLocation.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{defaultLocation.query || `${defaultLocation.latitude}, ${defaultLocation.longitude}`}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Lat {Number(defaultLocation.latitude).toFixed(4)}, Lon {Number(defaultLocation.longitude).toFixed(4)}</div>
          </div>
        )}
        <div className="card">
          <div className="form-group">
            <label className="form-label">Barn name</label>
            <input className="form-input" value={locationForm.name} onChange={e => setLocationForm({ ...locationForm, name: e.target.value })} placeholder="Home Barn" />
          </div>
          <div className="form-group">
            <label className="form-label">Address, city, ZIP, or latitude/longitude</label>
            <input className="form-input" value={locationForm.query} onChange={e => setLocationForm({ ...locationForm, query: e.target.value })} placeholder="e.g. 270 Triple R Dr, La Vernia, TX 78121" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Rural addresses can be spotty. The app will try the exact address first, then city/ZIP fallback. You can also enter latitude,longitude.
            </div>
          </div>
          <button className="btn btn-primary btn-full" onClick={saveLocation} disabled={savingLocation}>{savingLocation ? 'Saving…' : 'Save weather location'}</button>
        </div>

        <div className="section-label">Create weather rule</div>
        <div className="card">
          <div className="form-group">
            <label className="form-label">Rule name</label>
            <input className="form-input" value={ruleForm.name} onChange={e => setRuleForm({ ...ruleForm, name: e.target.value })} placeholder="e.g. Ride safety today" />
          </div>
          <div className="form-group">
            <label className="form-label">Trigger</label>
            <select className="form-input" value={ruleForm.trigger_type} onChange={e => {
              const next = triggerOptions.find(o => o.value === e.target.value);
              setRuleForm({
                ...ruleForm,
                trigger_type: e.target.value,
                threshold_value: '',
                lookahead_hours: next?.defaultLookahead || ruleForm.lookahead_hours,
                action_text: next?.defaultAction || ruleForm.action_text,
              });
            }}>
              {triggerOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {ruleForm.trigger_type === 'ride_heat_safety' && (
            <div style={{ background: 'var(--bg-secondary)', borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Ride safety / best ride window</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                This checks hourly temperature, humidity, calculated heat index, rain chance, storms, and wind. Home will show a short <strong>Weather Watch</strong> with the best ride window or a not-recommended notice.
              </div>
            </div>
          )}
          {selectedTrigger.needsThreshold && (
            <div className="form-group">
              <label className="form-label">Threshold</label>
              <input className="form-input" value={ruleForm.threshold_value} onChange={e => setRuleForm({ ...ruleForm, threshold_value: e.target.value })} placeholder={selectedTrigger.placeholder} inputMode="decimal" />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Look ahead</label>
              <select className="form-input" value={ruleForm.lookahead_hours} onChange={e => setRuleForm({ ...ruleForm, lookahead_hours: Number(e.target.value) })}>
                <option value={2}>Next 2 hours</option>
                <option value={4}>Next 4 hours</option>
                <option value={6}>Next 6 hours</option>
                <option value={12}>Next 12 hours</option>
                <option value={24}>Next 24 hours</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Notify before</label>
              <select className="form-input" value={ruleForm.notify_minutes_before} onChange={e => setRuleForm({ ...ruleForm, notify_minutes_before: Number(e.target.value) })}>
                <option value={0}>At time</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Home page action text</label>
            <select className="form-input" value={actionOptions.includes(ruleForm.action_text) ? ruleForm.action_text : 'Custom'} onChange={e => setRuleForm({ ...ruleForm, action_text: e.target.value === 'Custom' ? '' : e.target.value })}>
              {actionOptions.map(a => <option key={a} value={a}>{a}</option>)}
              <option value="Custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <input className="form-input" value={ruleForm.action_text} onChange={e => setRuleForm({ ...ruleForm, action_text: e.target.value })} placeholder="e.g. Bring horses in" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This is the short wording that appears on the Home page. For Ride Safety, the app adds the best ride window or heat warning underneath.</div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 10 }}>
            <input type="checkbox" checked={ruleForm.applies_to_all_horses} onChange={e => setRuleForm({ ...ruleForm, applies_to_all_horses: e.target.checked })} />
            Applies to all horses
          </label>
          {!ruleForm.applies_to_all_horses && (
            <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, padding: 10, marginBottom: 12 }}>
              {(data.horses || []).map(h => (
                <label key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '5px 0' }}>
                  <input type="checkbox" checked={ruleForm.horse_ids.includes(h.id)} onChange={() => toggleHorse(h.id)} />
                  {h.name}
                </label>
              ))}
            </div>
          )}
          <button className="btn btn-primary btn-full" onClick={createRule} disabled={savingRule || !defaultLocation}>{savingRule ? 'Saving…' : 'Save weather rule'}</button>
          {!defaultLocation && <div style={{ fontSize: 12, color: 'var(--cr-amber)', marginTop: 8 }}>Save a weather location before creating rules.</div>}
        </div>

        <div className="section-label">Active rules</div>
        {(data.rules || []).length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">🌦️</div><h3>No weather rules yet</h3><p>Create your first weather rule above.</p></div>
        ) : (data.rules || []).map(rule => (
          <div key={rule.id} className="card" style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{rule.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{triggerOptions.find(t => t.value === rule.trigger_type)?.label || rule.trigger_type}{rule.threshold_value ? ` — ${rule.threshold_value}${rule.threshold_unit || ''}` : ''}</div>
                <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 5 }}><strong>Home alert:</strong> {rule.action_text}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{rule.applies_to_all_horses ? 'All horses' : `${rule.horses?.length || 0} selected horses`} · next {rule.lookahead_hours || 6}h</div>
              </div>
              <button onClick={() => deleteRule(rule.id)} style={{ background: 'none', border: 'none', color: 'var(--cr-red)', fontSize: 18, cursor: 'pointer' }}>×</button>
            </div>
          </div>
        ))}

        <button className="btn btn-outline btn-full" onClick={checkNow} disabled={checking || !defaultLocation}>{checking ? 'Checking…' : 'Check weather rules now'}</button>
        {(data.events || []).length > 0 && (
          <>
            <div className="section-label">Current Weather Watch notices</div>
            {data.events.map(e => (
              <div key={e.id} className="card" style={{ border: '1px solid var(--cr-amber)', background: 'rgba(255, 193, 7, 0.12)' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--cr-amber)', textTransform: 'uppercase' }}>{weatherTitle(e)}</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{e.action_text}</div>
                {e.message && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{e.message}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{formatWeatherHorseSummary(e.horse_names)}</div>
                {Array.isArray(e.horse_names) && e.horse_names.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {e.horse_names.map(name => <span key={name} className="badge badge-amber">{name}</span>)}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEventAction(e.id, 'handled')}>Mark handled</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEventAction(e.id, 'snooze')}>Remind later</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEventAction(e.id, 'dismiss')}>Dismiss today</button>
                </div>
              </div>
            ))}
          </>
        )}

        <div className="card" style={{ background: 'var(--bg-secondary)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Weather data notice</div>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: 0 }}>
            Weather Watch uses third-party/public weather data sources, including weather.gov / National Weather Service where available. Forecasts and alerts may change, be delayed, or be unavailable. This feature is a convenience tool and should not replace your own judgment, local conditions, official emergency alerts, or barn safety decisions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WeatherWatchPage;
